import { useEffect, useState } from 'react';

/**
 * True while the viewport is at most `px` wide — the JS side of a CSS media query, for
 * the few places MARKUP must differ per viewport rather than styling (a toggle that only
 * exists on a phone, a label that names a key no phone has). Guarded for jsdom the way
 * `platform/theme.ts` established: no matchMedia means "not narrow", deterministically.
 *
 * ⚠️ Use CSS when CSS can do it. This hook exists for semantics — `aria-expanded` on a
 * desktop heading over always-visible content, or "Clear · Esc" offered to a thumb —
 * not for hiding boxes.
 */
export function useMaxWidth(px: number): boolean {
  const query = `(max-width: ${String(px)}px)`;
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const q = window.matchMedia(query);
    const on = (e: MediaQueryListEvent): void => {
      setNarrow(e.matches);
    };
    q.addEventListener('change', on);
    return () => {
      q.removeEventListener('change', on);
    };
  }, [query]);
  return narrow;
}
